import { BaseAction } from "../../../core/actions/BaseAction";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { addonFingerprint } from "../../../Lib/Utils/addons";
import { calculatePrice, isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import {
  AncillaryStatusEnum,
  TrainTypeEnum,
  TripTypeEnum,
} from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { isNolAncillary } from "../../../Lib/Utils/nolCard";
import { IBooking, IBookingResponse } from "../../../Orchestrator/Types/Index";
import { IBookedOffer, IReservation } from "../../../Orchestrator/Types/Interface/IOffer";
import {
  IAlreadyAddonsForPassenger,
  IAlreadyAddonsLegPassengerAddons,
} from "../../../Orchestrator/Types/Interface/IAddOns";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";

type IResult = { status: "success" | "fail"; message?: string };

export interface ISyncAlreadyAddonsParams {
  isUpgrade?: boolean;
  booking?: IBooking;
  /**
   * When true, include non-FULFILLED ancillaries even in manage-seating mode.
   * Callers writing the atom AFTER a persist pass this so newly-POSTed
   * PREBOOKED items are visible to the next confirm/toggle (diff detection
   * in ConfirmAddonsAction + OnChangeAddonForActivePassenger depends on
   * them being present). The default `false` applies the FULFILLED-only
   * belt-and-suspenders filter used at modal-open time.
   */
  includeNonFulfilledInManageSeating?: boolean;
  /**
   * When true, this sync is for read-only ticket-card display and must not
   * be influenced by global flow state it doesn't own. Stale flow signals
   * (e.g. a `CHANGE_DATE_FLOW` localStorage flag left over from an aborted
   * change-date attempt, or a lingering `isManageSeatingAtom`) survive SPA
   * navigation and would otherwise wrongly apply the change-date/manage-
   * seating FULFILLED-only filters here, and overwrite
   * `changeDateCarryOverSnapshotAtom`. Forces `isChangeDateFlow` and
   * `isManageSeating` to false for the duration of this run.
   */
  forDisplayOnly?: boolean;
}

/**
 * Populates `alreadyAddonsLegPassengerAtom` + `alreadyAddonsLegPassengerTotalAtom`
 * from the current `bookingResponseAtom` (or a booking passed in params). Used
 * whenever the addons flow starts — all flows that land on the addons modal
 * need this atom seeded so `PreparePassengerForAddons` can pre-check existing
 * addons.
 */
export class SyncAlreadyAddonsAction extends BaseAction<IResult, ISyncAlreadyAddonsParams> {
  protected override deduplicate = true;

  override async run(): Promise<IResult> {
    const bookingFromParams = this.params?.booking;
    const isUpgrade = this.params?.isUpgrade ?? false;
    const includeNonFulfilledInManageSeating =
      this.params?.includeNonFulfilledInManageSeating ?? false;
    const forDisplayOnly = this.params?.forDisplayOnly ?? false;
    const isManageSeating =
      ((getRecoilStateValue("isManageSeatingAtom") as boolean) || false) && !forDisplayOnly;
    // Change-date carry-over: the original-booking ancillaries that the
    // user can keep on the new prebooked offer. By design those are only the
    // `FULFILLED` and `EXCHANGE_ONGOING` items (paid/in-flight). REFUNDED
    // items are gone and must NOT be pre-selected. The standard offer
    // pipeline (getAllOfferWithoutExchangeAction → drop REFUNDED-admission
    // offers) already keeps offers whose admissions are EXCHANGE_ONGOING /
    // FULFILLED, which is exactly where carry-over ancillaries live.
    const isChangeDateFlow =
      typeof window !== "undefined" &&
      localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) === "true" &&
      !isManageSeating &&
      !isUpgrade &&
      !forDisplayOnly;
    const bookingResponse = bookingFromParams
      ? { booking: bookingFromParams }
      : getRecoilStateValue<IBookingResponse>("bookingResponseAtom");
    const booking = bookingResponse?.booking;
    if (!booking) return { status: "fail", message: "Missing booking" };

    let allBookedOfferWithoutExchange: IBookedOffer[] = booking?.bookedOffers || [];
    let allTripWithoutExchange: any[] = booking?.trips || [];

    getAllOfferWithoutExchangeAction.setParams(allBookedOfferWithoutExchange);
    await getAllOfferWithoutExchangeAction.execute();
    if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
      allBookedOfferWithoutExchange = getAllOfferWithoutExchangeAction.result || [];
      allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(
        (d) => (d.admissions?.[0]?.status || "") !== AncillaryStatusEnum.REFUNDED,
      );

      // In upgrade flow, only sync addons from the new PREBOOKED offers
      if (isUpgrade) {
        allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(
          (d) => d.admissions?.some((a) => a.status === AncillaryStatusEnum.PREBOOKED),
        );
      }
    }

    getAllTripWithoutExchangeAction.setParams({
      bookedOffers: allBookedOfferWithoutExchange,
      trips: allTripWithoutExchange,
    });
    await getAllTripWithoutExchangeAction.execute();
    if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
      allTripWithoutExchange = getAllTripWithoutExchangeAction.result || [];
    }

    const bookingPassengers = booking?.passengers || [];
    const alreadyAddonsLegPassenger: IAlreadyAddonsLegPassengerAddons = {};
    // Flatten once: post-exchange the NOL product an ancillary references can
    // live on a sibling offer, not just the offer that owns it.
    const allProducts = allBookedOfferWithoutExchange.flatMap((o: IBookedOffer) => o?.products || []);

    allBookedOfferWithoutExchange.forEach((bookedOffer: IBookedOffer) => {
      const bookTrip = allTripWithoutExchange.find(
        (t: any) => t.id === bookedOffer?.tripCoverage?.coveredTripId,
      );
      if (!bookTrip) return;

      const allAncillaryForTrip: IReservation[] = (bookedOffer.ancillaries || []).filter(
        (d: any) => {
          if (d?.tripCoverage?.coveredTripId !== bookTrip?.id) return false;
          if (d?.status === AncillaryStatusEnum.REFUNDED) return false;
          // NOL passes ride the booking as ancillaries, but they belong to the
          // End-to-end Journey flow — never the Extras preselection/diff. Without
          // this, manage-seating shows "1 Day Pass - Regular" under Add-ons and
          // the addons confirm could refund/re-book the pass as a meal toggle.
          if (isNolAncillary(allProducts, d)) return false;
          // Change-date carry-over set: by product spec, only FULFILLED and
          // EXCHANGE_ONGOING ancillaries qualify. PREBOOKED items are
          // mid-session items the user just toggled (handled via
          // confirmAddonsPassengerListAtom, not here); CONFIRMED is not a
          // carry-over status either. Strict whitelist so the Extras modal
          // never pre-selects a stale or already-spent item.
          if (
            isChangeDateFlow &&
            d?.status !== AncillaryStatusEnum.FULFILLED &&
            d?.status !== AncillaryStatusEnum.EXCHANGE_ONGOING
          ) {
            return false;
          }
          // Manage-seating entry deletes all PREBOOKED ancillaries
          // (ManageSeatingRedirectClickAction). Enforce FULFILLED-only here
          // as belt-and-suspenders so any PREBOOKED that slip through (partial
          // delete, race, re-fetch) don't get pre-selected and rendered as
          // "ghost" items in the Extras card / modal. Upgrade flow keeps the
          // original status filter (handled elsewhere). Callers writing the
          // atom after a persist opt out via `includeNonFulfilledInManageSeating`
          // so newly-POSTed PREBOOKED items remain visible to the next confirm.
          if (
            isManageSeating &&
            !includeNonFulfilledInManageSeating &&
            d?.status !== AncillaryStatusEnum.FULFILLED
          ) return false;
          return true;
        },
      );
      if (allAncillaryForTrip.length === 0) return;

      const legWiseAlreadyAddons: IAlreadyAddonsForPassenger[] = [];
      for (const ancillaries of allAncillaryForTrip) {
        const passengerDetails = bookingPassengers.find(
          (p: any) => p.id === (ancillaries?.passengerIds || [])[0],
        );
        const productId = (ancillaries?.products || [])[0]?.productId;
        const productDetails = (bookedOffer.products || []).find(
          (p: any) => p.id === productId,
        );
        const bookTripLeg = (bookTrip?.legs || []).find(
          (l: any) => l.id === (ancillaries?.tripCoverage?.coveredLegIds || [])[0],
        );

        const passenger: IAlreadyAddonsForPassenger = {
          category: ancillaries.type,
          addonName: productDetails?.description || productDetails?.summary || "",
          passengerId: passengerDetails?.id || "",
          passengerName: `${passengerDetails?.detail?.firstName || ""} ${
            passengerDetails?.detail?.lastName || ""
          }`,
          passengerRef: passengerDetails?.externalRef || "",
          type: productDetails?.code || productDetails?.id || productId || "",
          price: calculatePrice(ancillaries?.price) || 0,
          stopCode: `${bookTripLeg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || ""}-${
            bookTripLeg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || ""
          }`,
          legId: bookTripLeg?.id || "",
          ancillaryId: ancillaries.id,
          status: ancillaries?.status as AncillaryStatusEnum | undefined,
        };
        legWiseAlreadyAddons.push(passenger);
      }

      let direction = bookTrip?.direction || "";
      direction =
        direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound;

      alreadyAddonsLegPassenger[direction] = [
        ...(alreadyAddonsLegPassenger[direction] || []),
        ...legWiseAlreadyAddons,
      ];
    });

    updateRecoilState("alreadyAddonsLegPassengerAtom", alreadyAddonsLegPassenger);

    // Change-date only: publish a flat fingerprint snapshot of the carry-over
    // set so the Extras card can decide whether to surface a "NEW" tag on
    // each rendered addon. Each fingerprint is `${passengerRef}|${type}|${stopCode}`
    // — type is the product code (matches addonItem.type at the UI side) and
    // stopCode crosses the old→new legId boundary just like the matcher in
    // PreparePassengerForAddons. Other flows leave this atom alone — their
    // existing isPrebooked-based rule continues to apply.
    if (isChangeDateFlow) {
      const snapshot: string[] = [];
      const allCarryOver = [
        ...(alreadyAddonsLegPassenger[TrainTypeEnum.outbound] || []),
        ...(alreadyAddonsLegPassenger[TrainTypeEnum.inbound] || []),
      ];
      for (const addon of allCarryOver) {
        if (!addon.passengerRef || !addon.type || !addon.stopCode) continue;
        snapshot.push(addonFingerprint(addon.passengerRef, addon.type, addon.stopCode));
      }
      updateRecoilState("changeDateCarryOverSnapshotAtom", snapshot);
    }

    const totalAlreadyPaidPrice = [
      ...(alreadyAddonsLegPassenger?.outbound || []),
      ...(alreadyAddonsLegPassenger?.inbound || []),
    ];
    const legTotals = Object.values(
      totalAlreadyPaidPrice.reduce((acc, item: IAlreadyAddonsForPassenger) => {
        const key = item.legId ?? "UNKNOWN";
        acc[key] = acc[key] || { legId: key, totalPrice: 0 };
        acc[key].totalPrice += item.price || 0;
        return acc;
      }, {} as Record<string, { legId: string; totalPrice: number }>),
    );
    updateRecoilState("alreadyAddonsLegPassengerTotalAtom", legTotals);

    return { status: "success" };
  }
}

export const syncAlreadyAddonsAction = new SyncAlreadyAddonsAction();
